import type { DesiredSettingValue } from '@crossnokaye/typescript-sdk/models/components';

import type { DropdownOption, SettingAction } from '@repo/sdk/models/components';

import type { SettingChange } from '@/components/settings/settings-save.types';
import { createDesiredSettingValues } from '@/features/equipment-browser/action-components/helpers';
import type { FaultedDevice } from '@/features/equipment-browser/faulted-devices/faulted-devices.types';

import { FAULTED_STATUS_LABEL, MODELESS_RESET_LABEL } from './reset-faulted.constants';
import type { ModeValuesByDeviceId, ResetFaultedPayload } from './reset-faulted.types';

/** Mode options of a device's reset dialog; empty for modeless devices. */
export function getModeOptions(device: FaultedDevice): DropdownOption[] {
  return device.modeDropdown?.options ?? [];
}

/** The device's mode option with that value, if it has one. */
export function findModeOption(
  device: FaultedDevice,
  value: string | undefined
): DropdownOption | undefined {
  if (value === undefined) return undefined;
  return getModeOptions(device).find((option) => option.value === value);
}

/** True when the device's reset needs a mode choice. */
export function requiresMode(device: FaultedDevice): boolean {
  return getModeOptions(device).length > 0;
}

/**
 * Resolves the mode each device will run in after reset: the user's choice when
 * set, otherwise the device's current mode. Values that are not one of the
 * device's options are dropped, so the device reads as having no mode chosen.
 */
export function resolveTargetModes(
  devices: FaultedDevice[],
  currentModes: ModeValuesByDeviceId,
  chosenModes: ModeValuesByDeviceId
): ModeValuesByDeviceId {
  return Object.fromEntries(
    devices.flatMap((device) => {
      const deviceId = device.summary.referenceId;
      const candidate = chosenModes[deviceId] ?? currentModes[deviceId];
      return findModeOption(device, candidate) ? [[deviceId, candidate]] : [];
    })
  );
}

/** True when the device needs a mode and has none resolved yet. */
export function isMissingMode(device: FaultedDevice, targetModes: ModeValuesByDeviceId): boolean {
  return requiresMode(device) && !targetModes[device.summary.referenceId];
}

/**
 * A row can be selected when the device is resettable and, if it has modes,
 * a mode is resolved — the user picks one in the row to enable it.
 */
export function isSelectable(device: FaultedDevice, targetModes: ModeValuesByDeviceId): boolean {
  return device.isResettable && !isMissingMode(device, targetModes);
}

/**
 * The writes for one device's reset, merged the same way as the card's Reset
 * Fault confirmation: the button's setting actions, then the chosen mode option's.
 */
export function buildResetSettingActions(
  device: FaultedDevice,
  targetMode: string | undefined
): SettingAction[] {
  const modeOption = findModeOption(device, targetMode);
  return [...device.resetSettingActions, ...(modeOption?.actions?.settingActions ?? [])];
}

/** Label of the mode the device will run in after reset, or the modeless reset label. */
export function getTargetModeLabel(device: FaultedDevice, targetMode: string | undefined): string {
  if (!requiresMode(device)) return MODELESS_RESET_LABEL;
  const option = findModeOption(device, targetMode);
  return option?.label ?? targetMode ?? '';
}

/**
 * One confirm-dialog change per device: device → target mode, with the device's
 * merged writes as the payload. The change id is the device id so progress and
 * results map straight back to table rows.
 */
export function buildResetFaultedChanges(
  devices: FaultedDevice[],
  targetModes: ModeValuesByDeviceId
): SettingChange<ResetFaultedPayload>[] {
  return devices.map((device) => {
    const deviceId = device.summary.referenceId;
    const targetMode = targetModes[deviceId];
    const targetLabel = getTargetModeLabel(device, targetMode);
    const settings: DesiredSettingValue[] = createDesiredSettingValues(
      buildResetSettingActions(device, targetMode)
    );

    return {
      changeId: deviceId,
      settingId: deviceId,
      label: device.summary.title,
      currentValue: targetMode ?? '',
      displayCurrent: targetLabel,
      displayCurrentUnits: '',
      displayCurrentValue: targetLabel,
      displayOriginal: FAULTED_STATUS_LABEL,
      desiredValue: { deviceId, settings },
    };
  });
}

/** Maps every setting id written by a change back to its device id. */
export function mapSettingIdsToDeviceIds(
  changes: SettingChange<ResetFaultedPayload>[]
): Map<string, string> {
  return new Map(
    changes.flatMap((change) =>
      change.desiredValue.settings.map((setting): [string, string] => [
        setting.settingId,
        change.desiredValue.deviceId,
      ])
    )
  );
}
